import { useEffect, useState } from 'react'
import axios from 'axios'
import { useDispatch, useSelector } from 'react-redux'
// import { initDunamis } from '../analytics'
// import { sendLoginAnalytics } from '../helpers/analytics-helpers'
import ContactCenter from '../Apps/ContactCenter/Modules/ContactCenter'
import config from '../config/env_variables'
import { AGENT_DEFAULT_STATUS, AGENT_OFFLINE_STATUS } from '../Apps/ContactCenter/Constants'
import { setLoadingStage } from '../redux/actions/preferencesActions'
import { LOADING_STAGES } from '../redux/constants/preferencesConstants'
import { RootState } from '../redux/reducers/rootReducer'

const agentDefaultStatus = {
  status: AGENT_DEFAULT_STATUS
}

const useOnLoad = (oktaAuth: any, shouldInitialize: boolean = true, hasRequiredPermissions: boolean = true) => {
  const dispatch = useDispatch()
  const [authState, setAuthState] = useState<any | null>(null)
  const isCCPInitialized = useSelector((state: RootState) => state.contactCenter.CCPStatus.isInit)
  
  useEffect(() => {
    // Get initial auth state
    const updateAuthState = async () => {
      const state = await oktaAuth.authStateManager.getAuthState()
      setAuthState(state)
    }
    updateAuthState()
    
    // Subscribe to auth state changes
    oktaAuth.authStateManager.subscribe(updateAuthState)
    
    return () => {
      oktaAuth.authStateManager.unsubscribe(updateAuthState)
    }
  }, [oktaAuth])

  const onUnload = (e: any) => {
    ContactCenter.saveActiveChatIds()
    const agentState: any = ContactCenter.getAgentStatus()
    sessionStorage.setItem('agent_currentStatus', agentState.currentState)
    sessionStorage.setItem('agent_nextStatus', agentState.nextState)
    const close = ContactCenter.isLogoutAllowed()

    if (!close) {
      ContactCenter.updateAgentStatus(agentDefaultStatus.status, '')
      e.preventDefault()
      e.returnValue = 'Are you sure you want to leave?'
      setTimeout(() => {
        const previousStatus = sessionStorage.getItem('agent_currentStatus')
        let nextStatus = sessionStorage.getItem('agent_nextStatus')
        if (previousStatus) {
          nextStatus = nextStatus || ''
          ContactCenter.updateAgentStatus(previousStatus, nextStatus)
        }
      }, 1000)
    } else {
      // If logout is allowed, set agent status to Offline when tab is closed
      ContactCenter.closeTab()
    }
  }
  const onLoad = () => {
    if (Notification.permission !== 'granted') Notification.requestPermission()
  }

  useEffect(() => {
    let profileInfo = {
      name: '',
      email: '',
      preferred_username: ''
    }
    // initiate dunamis
    // initDunamis()
    window.addEventListener('beforeunload', onUnload)
    window.addEventListener('load', onLoad)

    const getOktaInfo = async () => {
      // Don't initialize if shouldInitialize is false
      if (!shouldInitialize) {
        console.log('DEBUG [AUTHENTICATION]- Initialization disabled, skipping...')
        dispatch(setLoadingStage(LOADING_STAGES.IDLE))
        return
      }
      
      // Don't initialize if required permissions are not granted
      if (!hasRequiredPermissions) {
        console.log('DEBUG [PERMISSIONS]- Required browser permissions not granted, skipping authentication and CCP initialization...')
        dispatch(setLoadingStage(LOADING_STAGES.IDLE))
        return
      }
      try {
        // Check if user has access token before calling getUser()
        // This prevents "getUserInfo requires an access token" errors
        if (!authState || !authState.isAuthenticated || !authState.accessToken) {
          console.log('User is not authenticated or access token not available yet')
          dispatch(setLoadingStage(LOADING_STAGES.AUTHENTICATING))
          return
        }

        // Stage 1: SAML Login - Getting user info from Okta
        dispatch(setLoadingStage(LOADING_STAGES.SAML_LOGIN))
        const OktaData = await oktaAuth.getUser().catch((error: any) => {
          // Handle getUserInfo errors gracefully
          if (error?.errorCode === 'INTERNAL' && error?.message?.includes('getUserInfo')) {
            console.log('getUserInfo error - access token not available:', error.message)
          } else {
            console.log('getUser error:', error)
          }
          dispatch(setLoadingStage(LOADING_STAGES.IDLE))
          return null
        })

        // sendLoginAnalytics({
        //   subCategory: 'Implicit',
        //   type: 'success'
        // })
        
        if (OktaData) {
          profileInfo = { ...profileInfo, ...OktaData }
          const oktaToken: any = JSON.parse(localStorage.getItem('okta-token-storage') || '')
          if (oktaToken?.accessToken?.accessToken) {
            // Stage 2: Backend Authentication
            dispatch(setLoadingStage(LOADING_STAGES.BACKEND_AUTH))
            const {
              data: { data: { json: { accessToken = '', customPanelAccessToken = '', fastFollowFlags = '' } = {} } = {} }
            }: any = await axios.get(`${config.CCP_CH_URL}/api/auth/v1/authenticate`, {
              params: {
                accessToken: oktaToken?.accessToken?.accessToken
              },
              headers: { 'x-api-key': 'yoddha-service' }
            })
            localStorage.setItem('ims-token', JSON.stringify(accessToken))
            localStorage.setItem('customPanelAccessToken', JSON.stringify(customPanelAccessToken))
            localStorage.setItem('fastFollowFlags', JSON.stringify(fastFollowFlags))
            
            // Stage 3: CCP Initialization
            // Only initialize CCP if it hasn't been initialized yet to prevent duplicate conversations
            if (!isCCPInitialized) {
              dispatch(setLoadingStage(LOADING_STAGES.CCP_INITIALIZING))
              new ContactCenter(profileInfo)
            } else {
              console.log('DEBUG [AUTHENTICATION]- CCP already initialized, skipping ContactCenter instantiation to prevent duplicates')
              dispatch(setLoadingStage(LOADING_STAGES.COMPLETE))
            }
          }
        } else {
          dispatch(setLoadingStage(LOADING_STAGES.IDLE))
          // sendLoginAnalytics({
          //   subCategory: 'Implicit',
          //   type: 'failure'
          // })
        }
      } catch (error: any) {
        console.log('useOnLoad error:', error)
        dispatch(setLoadingStage(LOADING_STAGES.IDLE))
        // sendLoginAnalytics({
        //   subCategory: 'Implicit',
        //   type: 'failure'
        // })
      }
    }

    // Call immediately in case user is already authenticated
    getOktaInfo()

    return () => {
      window.removeEventListener('beforeunload', onUnload)
      window.removeEventListener('load', onLoad)
    }
  }, [authState, shouldInitialize, hasRequiredPermissions])
}

export default useOnLoad
